/**
 * 卖家精灵告警后台视图 — 账号状态 / 飞书配置 / 告警日志 / 重检测 / 重跑采集
 * 仅管理员可访问。
 */
import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { showToast, formatDate, renderPagination } from './utils.js';

let initialized = false;
let eventsBound = false;
let logsState = { page: 1, limit: 20 };

function createAlertElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = String(text);
  return element;
}

function replaceAlertChildren(element, ...children) {
  if (element) element.replaceChildren(...children);
  return element;
}

function createAlertMessage(className, text) {
  return createAlertElement('div', className, text);
}

function createAlertBadge(className, text) {
  return createAlertElement('span', `pr-alert-badge ${className}`, text);
}

function createAlertStat(label, value, className = '') {
  const stat = createAlertElement('div', `pr-alert-stat${className ? ` ${className}` : ''}`);
  const content = createAlertElement('div', 'pr-alert-stat-value');
  if (Array.isArray(value)) content.append(...value);
  else if (value instanceof Node) content.append(value);
  else content.textContent = String(value ?? '—');
  stat.append(createAlertElement('div', 'pr-alert-stat-label', label), content);
  return stat;
}

// 统一 API 请求：后端返回 { success, data } 或 { success: false, error }
async function apiGet(url) {
  const resp = await authFetch(url);
  const json = await resp.json();
  if (!resp.ok || !json.success) throw new Error(json.error || '请求失败');
  return json.data;
}

async function apiPost(url, body) {
  const resp = await authFetch(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await resp.json();
  if (!resp.ok || !json.success) throw new Error(json.error || '请求失败');
  return json.data;
}

export function initProductResearchAlert() {
  if (!initialized) {
    initialized = true;
    registerViewLoader('admin-product-research-alert', () => {
      loadAccountStatus();
      loadLogs();
    });
  }

  if (eventsBound || !document.getElementById('view-admin-product-research-alert')) return;
  bindEvents();
  eventsBound = true;
}

function bindEvents() {
  const $ = (id) => document.getElementById(id);
  if (!$('pr-alert-verify-login')) return;

  $('pr-alert-verify-login').addEventListener('click', verifyLogin);
  $('pr-alert-retry-login').addEventListener('click', retryLogin);
  $('pr-alert-refresh').addEventListener('click', loadAccountStatus);
  $('pr-alert-test-feishu').addEventListener('click', testFeishuAlert);
  $('pr-alert-rerun-collection').addEventListener('click', rerunCollection);
  $('pr-alert-logs-refresh').addEventListener('click', () => loadLogs(1));
  $('pr-alert-filter-level').addEventListener('change', () => loadLogs(1));
  $('pr-alert-filter-status').addEventListener('change', () => loadLogs(1));
  $('pr-alert-filter-days').addEventListener('change', () => loadLogs(1));
}

async function loadAccountStatus() {
  const container = document.getElementById('pr-alert-account-status');
  const feishuContainer = document.getElementById('pr-alert-feishu-config');
  if (container) replaceAlertChildren(container, createAlertMessage('pr-alert-loading', '加载中...'));
  if (feishuContainer) replaceAlertChildren(feishuContainer, createAlertMessage('pr-alert-loading', '加载中...'));

  try {
    const data = await apiGet('/api/admin/product-research-alert/account-status');
    renderAccountStatus(data);
    renderFeishuConfig(data.feishu);
  } catch (err) {
    if (container) replaceAlertChildren(container, createAlertMessage('pr-alert-error', `⚠️ ${err.message}`));
    if (feishuContainer) replaceAlertChildren(feishuContainer, createAlertMessage('pr-alert-error', `⚠️ ${err.message}`));
  }
}

function renderAccountStatus(data) {
  const container = document.getElementById('pr-alert-account-status');
  if (!container) return;

  const { status, fileState, credentials, cooldown } = data;
  const statusBadge = renderStatusBadge(status?.status);
  // stale 标记：本地 cookie 存在但保存时间较久，服务端会话可能已失效
  const fileBadge = fileState?.configured
    ? createAlertBadge(fileState.stale ? 'pr-alert-badge-warn' : 'pr-alert-badge-ok', fileState.stale ? '可能已过期' : '已配置')
    : createAlertBadge('pr-alert-badge-failed', '未配置');
  const fileSavedAt = fileState?.savedAt ? formatDate(fileState.savedAt) : '—';
  const fileSaved = [fileSavedAt];
  if (fileState?.stale && fileState?.ageHours != null) fileSaved.push(document.createTextNode(' '), createAlertBadge('pr-alert-badge-warn', `${fileState.ageHours}h 前`));
  // 冷却状态：登录失败后递增冷却，防止频繁登录触发 sellersprite 子账号锁定
  const cooldownBadge = cooldown?.inCooldown
    ? createAlertBadge('pr-alert-badge-warn', `冷却中（约 ${Math.ceil(cooldown.remainingSeconds / 60)} 分钟后可重试）`)
    : createAlertBadge('pr-alert-badge-ok', '可重试');
  const errorType = status?.last_error_type || '—';
  const errorMsg = status?.last_error_message || '—';
  const lastSuccess = status?.last_login_success_time ? formatDate(status.last_login_success_time) : '—';
  const lastFailed = status?.last_login_failed_time ? formatDate(status.last_login_failed_time) : '—';
  const lastCheck = status?.last_session_check_time ? formatDate(status.last_session_check_time) : '—';
  const failureCount = status?.failure_count != null ? status.failure_count : 0;

  const errorMessage = createAlertElement('div', 'pr-alert-stat-value pr-alert-stat-msg', errorMsg);
  const errorStat = createAlertElement('div', 'pr-alert-stat pr-alert-stat-wide');
  errorStat.append(createAlertElement('div', 'pr-alert-stat-label', '最近失败原因'), errorMessage);
  replaceAlertChildren(container,
    createAlertStat('账号状态', statusBadge), createAlertStat('文件登录态', fileBadge), createAlertStat('重新登录冷却', cooldownBadge),
    createAlertStat('脱敏账号', credentials?.accountMask || '未配置'), createAlertStat('连续失败次数', failureCount), createAlertStat('文件保存时间', fileSaved),
    createAlertStat('最近自检时间', lastCheck), createAlertStat('最近登录成功', lastSuccess), createAlertStat('最近登录失败', lastFailed),
    createAlertStat('最近失败类型', errorType, 'pr-alert-stat-wide'), errorStat,
  );
}

function renderStatusBadge(status) {
  const map = {
    ok:          { label: '正常',   cls: 'pr-alert-badge-ok' },
    login_failed:{ label: '登录失败', cls: 'pr-alert-badge-failed' },
    network_unavailable: { label: '网络不可达', cls: 'pr-alert-badge-warn' },
    unknown:     { label: '未知',   cls: 'pr-alert-badge-unknown' },
  };
  const info = map[status] || map.unknown;
  return createAlertBadge(info.cls, info.label);
}

function renderFeishuConfig(feishu) {
  const container = document.getElementById('pr-alert-feishu-config');
  if (!container) return;

  const configuredBadge = createAlertBadge(feishu?.configured ? 'pr-alert-badge-ok' : 'pr-alert-badge-failed', feishu?.configured ? '已配置' : '未配置');
  const enabledBadge = createAlertBadge(feishu?.enabled ? 'pr-alert-badge-ok' : 'pr-alert-badge-unknown', feishu?.enabled ? '已启用' : '已禁用');
  replaceAlertChildren(container,
    createAlertStat('Webhook', configuredBadge), createAlertStat('告警开关', enabledBadge),
    createAlertStat('Webhook 地址（脱敏）', feishu?.webhookUrl || '—', 'pr-alert-stat-wide'),
  );
}

async function verifyLogin() {
  const feedback = document.getElementById('pr-alert-account-feedback');
  if (feedback) feedback.textContent = '正在检测登录态...';
  try {
    const result = await apiPost('/api/admin/product-research-alert/verify-login');
    if (result.healthy) {
      showToast(`登录态正常${result.refreshed ? '（已自动刷新）' : ''}`, 'success');
      if (feedback) feedback.textContent = `✅ 登录态正常，耗时 ${result.durationMs}ms`;
    } else {
      showToast('登录态异常，请查看告警', 'error');
      if (feedback) feedback.textContent = '⚠️ 登录态异常';
    }
    await loadAccountStatus();
  } catch (err) {
    showToast(`检测失败：${err.message}`, 'error');
    if (feedback) feedback.textContent = `⚠️ ${err.message}`;
  }
}

async function retryLogin() {
  const feedback = document.getElementById('pr-alert-account-feedback');
  if (feedback) feedback.textContent = '正在打开浏览器重新登录...';
  try {
    const result = await apiPost('/api/admin/product-research-alert/retry-login');
    showToast('登录态已刷新', 'success');
    if (feedback) feedback.textContent = `✅ 登录态：${result.configured ? '已配置' : '未配置'}`;
    await loadAccountStatus();
  } catch (err) {
    // 冷却期内拒绝：err.message 已包含剩余时间提示
    showToast(`登录失败：${err.message}`, 'error');
    if (feedback) feedback.textContent = `⚠️ ${err.message}`;
    await loadAccountStatus(); // 刷新冷却状态显示
  }
}

async function testFeishuAlert() {
  const feedback = document.getElementById('pr-alert-test-feedback');
  if (feedback) feedback.textContent = '正在发送测试告警...';
  try {
    const result = await apiPost('/api/admin/product-research-alert/test-alert');
    if (result.sent) {
      showToast('测试告警已发送', 'success');
      if (feedback) feedback.textContent = '✅ 已发送，请检查飞书群';
    } else {
      showToast(`发送失败：${result.error}`, 'error');
      if (feedback) feedback.textContent = `⚠️ ${result.error}`;
    }
  } catch (err) {
    showToast(`发送失败：${err.message}`, 'error');
    if (feedback) feedback.textContent = `⚠️ ${err.message}`;
  }
}

async function rerunCollection() {
  const feedback = document.getElementById('pr-alert-rerun-feedback');
  if (feedback) feedback.textContent = '正在提交采集任务...';
  try {
    const result = await apiPost('/api/admin/product-research-alert/rerun-collection', {
      target_count: 600
    });
    showToast('采集任务已提交', 'success');
    if (feedback) feedback.textContent = `✅ 任务ID：${result.job_id || '已复用'}，队列位置：${result.queue_position ?? '—'}`;
  } catch (err) {
    showToast(`提交失败：${err.message}`, 'error');
    if (feedback) feedback.textContent = `⚠️ ${err.message}`;
  }
}

async function loadLogs(page = logsState.page) {
  logsState.page = page;
  const $ = (id) => document.getElementById(id);
  const listEl = $('pr-alert-logs-list');
  const paginationEl = $('pr-alert-logs-pagination');
  if (listEl) replaceAlertChildren(listEl, createAlertMessage('pr-alert-loading', '加载中...'));
  if (paginationEl) replaceAlertChildren(paginationEl);

  const params = new URLSearchParams();
  params.set('page', logsState.page);
  params.set('limit', logsState.limit);
  const level = $('pr-alert-filter-level')?.value;
  const status = $('pr-alert-filter-status')?.value;
  const days = $('pr-alert-filter-days')?.value;
  if (level) params.set('level', level);
  if (status) params.set('send_status', status);
  if (days) params.set('days', days);

  try {
    const data = await apiGet(`/api/admin/product-research-alert/logs?${params}`);
    renderLogs(data);
    if (data.total > data.limit) {
      renderPagination('pr-alert-logs-pagination', data.total, data.page, data.limit, (p) => loadLogs(p));
    }
  } catch (err) {
    if (listEl) replaceAlertChildren(listEl, createAlertMessage('pr-alert-error', `⚠️ ${err.message}`));
  }
}

function renderLogs(data) {
  const listEl = document.getElementById('pr-alert-logs-list');
  if (!listEl) return;

  if (!data.items || data.items.length === 0) {
    const empty = createAlertElement('div', 'empty-state');
    empty.append(createAlertElement('div', 'empty-icon', '📭'), createAlertElement('p', '', '暂无告警日志'));
    replaceAlertChildren(listEl, empty);
    return;
  }

  const rows = data.items.map((log) => {
    const levelBadge = createAlertBadge(log.alert_level === 'critical' ? 'pr-alert-badge-failed' : 'pr-alert-badge-unknown', log.alert_level === 'critical' ? 'CRITICAL' : 'WARNING');
    const statusBadge = renderSendStatusBadge(log.send_status);
    const createdAt = formatDate(log.created_at);
    const row = document.createElement('tr');
    const title = createAlertElement('td', '', log.title || '—');
    title.title = String(log.title || '');
    const error = document.createElement('td');
    if (log.error_message) error.append(createAlertElement('span', 'pr-alert-error-msg', log.error_message));
    else error.textContent = '—';
    const appendCell = (value) => {
      const cell = document.createElement('td');
      if (value instanceof Node) cell.append(value);
      else cell.textContent = String(value ?? '—');
      row.append(cell);
    };
    appendCell(createdAt);
    appendCell(levelBadge);
    appendCell(statusBadge);
    appendCell(log.alert_type || '—');
    row.append(title, error);
    return row;
  });
  const table = createAlertElement('table', 'data-table');
  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  ['时间', '级别', '状态', '类型', '标题', '错误信息'].forEach((label) => headRow.append(createAlertElement('th', '', label)));
  head.append(headRow);
  const body = document.createElement('tbody');
  body.append(...rows);
  table.append(head, body);
  replaceAlertChildren(listEl, table);
}

function renderSendStatusBadge(status) {
  const map = {
    success:    { label: '已发送',  cls: 'pr-alert-badge-ok' },
    failed:     { label: '发送失败', cls: 'pr-alert-badge-failed' },
    suppressed: { label: '已抑制',  cls: 'pr-alert-badge-unknown' },
    disabled:   { label: '已禁用',  cls: 'pr-alert-badge-unknown' },
    pending:    { label: '待发送',  cls: 'pr-alert-badge-unknown' },
  };
  const info = map[status] || { label: status || '—', cls: 'pr-alert-badge-unknown' };
  return createAlertBadge(info.cls, info.label);
}
